import React from 'react';
import { Search, Filter, X } from 'lucide-react';

/**
 * LeadFilters - Filter bar component for the CRM pipeline
 * @param {String} searchQuery - Current search query
 * @param {Function} setSearchQuery - Set search query
 * @param {String} stageFilter - Current stage filter
 * @param {Function} setStageFilter - Set stage filter
 * @param {String} sourceFilter - Current source filter
 * @param {Function} setSourceFilter - Set source filter
 * @param {String} assignedFilter - Current assigned filter
 * @param {Function} setAssignedFilter - Set assigned filter
 * @param {String} dateRangeFilter - Current date range filter
 * @param {Function} setDateRangeFilter - Set date range filter
 * @param {Boolean} showFilters - Whether to show expanded filters
 * @param {Function} setShowFilters - Toggle expanded filters
 * @param {Array} staff - List of staff for assignment filter
 * @param {Function} onClearFilters - Clear all filters
 */
const LeadFilters = ({
  searchQuery,
  setSearchQuery,
  stageFilter,
  setStageFilter,
  sourceFilter,
  setSourceFilter,
  assignedFilter,
  setAssignedFilter,
  dateRangeFilter,
  setDateRangeFilter,
  showFilters,
  setShowFilters,
  staff = [],
  onClearFilters
}) => {
  // Pipeline stages
  const stages = [
    { name: 'new', label: 'New Lead' },
    { name: 'contacted', label: 'Contacted' },
    { name: 'tour_scheduled', label: 'Tour Scheduled' },
    { name: 'tour_completed', label: 'Tour Completed' },
    { name: 'enrollment_pending', label: 'Enrollment Pending' },
    { name: 'enrolled', label: 'Enrolled' },
    { name: 'lost', label: 'Lost' }
  ];

  // Lead sources
  const sources = [
    { value: 'website', label: 'Website' },
    { value: 'referral', label: 'Referral' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'walk_in', label: 'Walk-in' },
    { value: 'phone_inquiry', label: 'Phone Inquiry' },
    { value: 'email_inquiry', label: 'Email Inquiry' },
    { value: 'advertisement', label: 'Advertisement' },
    { value: 'event', label: 'Event' },
    { value: 'other', label: 'Other' }
  ];

  // Date ranges
  const dateRanges = [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' }
  ];

  const hasActiveFilters = searchQuery || stageFilter || sourceFilter || assignedFilter || dateRangeFilter;

  const activeFilterCount = [
    searchQuery,
    stageFilter,
    sourceFilter,
    assignedFilter,
    dateRangeFilter
  ].filter(Boolean).length;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-input pl-12 w-full"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filter Toggle */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center space-x-2 px-4 py-3 rounded-lg border transition-colors ${
            showFilters || hasActiveFilters
              ? 'bg-purple-50 border-purple-200 text-purple-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Filter size={18} />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-purple-600 text-white text-xs rounded-full px-2 py-0.5">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Expanded Filters */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
          {/* Stage Filter */}
          <div>
            <label className="form-label">Stage</label>
            <select
              value={stageFilter}
              onChange={(e) => setStageFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Stages</option>
              {stages.map(stage => (
                <option key={stage.name} value={stage.name}>{stage.label}</option>
              ))}
            </select>
          </div>

          {/* Source Filter */}
          <div>
            <label className="form-label">Source</label>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Sources</option>
              {sources.map(source => (
                <option key={source.value} value={source.value}>{source.label}</option>
              ))}
            </select>
          </div>

          {/* Assigned Filter */}
          <div>
            <label className="form-label">Assigned To</label>
            <select
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Staff</option>
              <option value="unassigned">Unassigned</option>
              {staff.map(member => (
                <option key={member.id} value={member.id}>
                  {member.first_name} {member.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range Filter */}
          <div>
            <label className="form-label">Date Range</label>
            <select
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              className="form-input"
            >
              <option value="">All Time</option>
              {dateRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Active Filter Tags */}
      {hasActiveFilters && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100 items-center">
          <span className="text-sm text-gray-500">Active filters:</span>
          
          {searchQuery && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Search: {searchQuery}
              <button 
                onClick={() => setSearchQuery('')} 
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          
          {stageFilter && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Stage: {stages.find(s => s.name === stageFilter)?.label}
              <button 
                onClick={() => setStageFilter('')} 
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          
          {sourceFilter && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Source: {sources.find(s => s.value === sourceFilter)?.label}
              <button 
                onClick={() => setSourceFilter('')} 
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          
          {assignedFilter && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Assigned: {assignedFilter === 'unassigned' 
                ? 'Unassigned' 
                : `${staff.find(s => s.id === assignedFilter)?.first_name} ${staff.find(s => s.id === assignedFilter)?.last_name?.[0]}.`}
              <button 
                onClick={() => setAssignedFilter('')} 
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          
          {dateRangeFilter && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-purple-100 text-purple-700">
              Date: {dateRanges.find(r => r.value === dateRangeFilter)?.label}
              <button 
                onClick={() => setDateRangeFilter('')} 
                className="ml-1 hover:text-purple-900"
              >
                <X size={12} />
              </button>
            </span>
          )}
          
          <button
            onClick={onClearFilters}
            className="text-xs text-red-600 hover:text-red-700 font-medium ml-2"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

export default LeadFilters;
